import { FormGroup, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPerson } from '@common/utils/getDisplayName';
import type { OsasEvaluationData } from '@common/validation/customControls/osas/osasEvaluationSchemas/types';
import type { IReduxState } from '@frontend/configureStore';
import { CustomControlCode, Profession, ServiceCode } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { findSingleProcessByServiceCode, getActivityPerformer } from '@common/utils/processUtil';
import { getPractitionersInOrganization } from '@frontend/services/api/employmentsApiService';
import { useProcessActivityEditContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useOsasAllTreatments } from '@frontend/utils/hooks/useOsasAllTreatments';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { AnamnesisFormSection } from './components/AnamnesisFormSection';
import { InformFormSection } from './components/InformFormSection';
import { PolicyFormSection } from './components/PolicyFormSection';
import { TreatmentChoiceFormSection } from './components/TreatmentChoiceFormSection';
import { calculateTreatmentChoice } from './utils/treatmentChoiceUtils';

export function OsasEvaluationEdit() {
    const abortSignal = useAbortSignal();
    const { data, onChange } = useProcessActivityEditContext<OsasEvaluationData>();
    const { processes } = usePatientEpisodeCategoryContext();
    const process = findSingleProcessByServiceCode(processes, ServiceCode.OSAS);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const [treatingPractitionerOptions, setTreatingPractitionerOptions] = useState<{ label: string; value: string }[]>(
        []
    );
    const { onChangeTrialPeriod, inProgressTreatments } = useOsasAllTreatments({ onChange });

    useEffect(() => {
        getTreatingPhysicianOptions();
    }, []);

    useEffect(() => {
        if (!process) {
            return;
        }

        const consult = getActivityPerformer(process, CustomControlCode.OSASConsult);

        if (!consult) {
            return;
        }

        onChange(
            {
                treatingPhysicianPractitionerId: consult.performerPractitioner?.id ?? null,
                treatmentChoice: calculateTreatmentChoice(inProgressTreatments),
            },
            ['treatingPhysicianPractitionerId']
        );
    }, [process, inProgressTreatments]);

    useEffect(() => {
        onChange(
            { treatmentChoice: calculateTreatmentChoice(inProgressTreatments, data.treatmentChoice) },
            'treatmentChoice'
        );
    }, [inProgressTreatments]);

    return (
        <>
            <FormGroup label="Behandelend arts">
                <Select
                    isClearable={true}
                    onChange={async (option) =>
                        onChange(
                            { treatingPhysicianPractitionerId: option?.value ?? null },
                            'treatingPhysicianPractitionerId'
                        )
                    }
                    options={treatingPractitionerOptions}
                    placeholder="Selecteer een zorgverlener..."
                    value={treatingPractitionerOptions.find(
                        (option) => option.value === data.treatingPhysicianPractitionerId
                    )}
                />
            </FormGroup>

            <TreatmentChoiceFormSection
                hasTreatmentSections={true}
                onChangeTrialPeriod={onChangeTrialPeriod}
            />
            <AnamnesisFormSection onChangeTrialPeriod={onChangeTrialPeriod} />
            <PolicyFormSection />
            <InformFormSection />
        </>
    );

    async function getTreatingPhysicianOptions() {
        const employments = await getPractitionersInOrganization(
            organizationId,
            [Profession.medicalSpecialist],
            abortSignal
        );

        const options = employments.map((employment) => ({
            label: getDisplayName(employment.practitioner as IPerson),
            value: employment.practitioner.id,
        }));

        setTreatingPractitionerOptions(options);
    }
}
